/**
 * The main different compared to the DatepickerField.js is that dropdown values are static, without any references to the selected year/month/day
 * */

import { html } from 'lit-element';
import moment from 'moment';
import { ifDefined } from 'lit-html/directives/if-defined';

import connect from '@elab/core/store/connect';
import getSelectors from '@elab/core/utils/dynaForms/selectors';
import { datePickerConstants } from '@elab/core/utils/constants';
import { getDateByChunkOfFormat, getDateChunkIndex } from '@elab/core/utils/DateUtils';
import { selectors as globalSelectors } from '@elab/core/store/global';
import * as configSelectors from '@elab/core/store/config/selectors';
import { cacheSelector } from '@elab/core/utils/webComponent';

import { FormElement } from './Form';

const { DEFAULT_START_YEAR, FORMAT_PANELS, SELECT_ORDER } = datePickerConstants;

export class StaticDatePickerField extends FormElement {
    static get properties() {
        return {
            required: { type: Boolean, reflect: true },
            hideField: { type: Boolean, reflect: true },

            yearLabel: { type: String, attribute: 'year-label' },
            monthLabel: { type: String, attribute: 'month-label' },
            dayLabel: { type: String, attribute: 'day-label' },
            placeholder: { type: String },

            januaryLabel: { type: String, attribute: 'january-label' },
            februaryLabel: { type: String, attribute: 'february-label' },
            marchLabel: { type: String, attribute: 'march-label' },
            aprilLabel: { type: String, attribute: 'april-label' },
            mayLabel: { type: String, attribute: 'may-label' },
            juneLabel: { type: String, attribute: 'june-label' },
            julyLabel: { type: String, attribute: 'july-label' },
            augustLabel: { type: String, attribute: 'august-label' },
            septemberLabel: { type: String, attribute: 'september-label' },
            octoberLabel: { type: String, attribute: 'october-label' },
            novemberLabel: { type: String, attribute: 'november-label' },
            decemberLabel: { type: String, attribute: 'december-label' },

            errorMessage: { type: String, attribute: 'error-message' },
            hasErrors: {
                type: Boolean,
                attribute: 'has-errors',
                reflect: true,
            },

            blurTimeoutID: { type: Number, attribute: false },
            hideCaption: { type: Boolean, attribute: false },
            maxRange: { type: Number, attribute: 'max-range' },
            gtmLabel: {
                type: String,
                attribute: 'gtm-label',
            },
            useLabelAsPlaceholder: {
                type: Boolean,
                attribute: 'use-label-as-placeholder',
            },
            requiredMarkAsPartOfLabel: {
                type: Boolean,
                attribute: 'required-mark-as-part-of-label',
            },
            disabledNativePlaceholderOption: {
                type: Boolean,
                attribute: 'disabled-native-placeholder-option',
            },
            dobSortDescending: {
                type: Boolean,
                attribute: false,
            },
        };
    }

    constructor() {
        super();

        this.errorMessage = '';
        this.hasErrors = false;
        this.blurTimeoutID = 0;
        this.maxRange = 0;
        this.gtmLabel = '';
        this.useLabelAsPlaceholder = false;
        this.requiredMarkAsPartOfLabel = false;
        this.disabledNativePlaceholderOption = false;
        this.hideCaption = false;
        this.dobSortDescending = false;
    }

    updated() {
        this.hasErrors = this.isErrorDefined();
    }

    static padDateChunk(date, index) {
        const padValues = [4, 2, 2];
        return date && `${date}`.padStart(padValues[index], '0');
    }

    getChunkOfDate(chunkName) {
        const date = this.fieldValue && this.fieldValue.value;
        return date ? getDateByChunkOfFormat(date, chunkName) || '' : '';
    }

    setChunkOfDate(chunkName, value) {
        const date = [this.year, this.month, this.day];

        const chunkIndex = getDateChunkIndex(chunkName);
        date[chunkIndex] = value;

        const transformedDate = date.map(StaticDatePickerField.padDateChunk);
        const formattedDate = transformedDate.some(value => !!value)
            ? transformedDate.join('-')
            : null;

        const allChunksFilled = transformedDate.every(value => !!value);

        if (allChunksFilled) {
            this.handleBlur(); // if all date chunks are filled - it's the same as blur
        }

        this.changeValue(formattedDate);
    }

    get year() {
        return this.getChunkOfDate(FORMAT_PANELS.YEARS);
    }

    set year(value) {
        this.setChunkOfDate(FORMAT_PANELS.YEARS, value);
    }

    get month() {
        return this.getChunkOfDate(FORMAT_PANELS.MONTHS);
    }

    set month(value) {
        this.setChunkOfDate(FORMAT_PANELS.MONTHS, value);
    }

    get day() {
        return this.getChunkOfDate(FORMAT_PANELS.DAYS);
    }

    set day(value) {
        this.setChunkOfDate(FORMAT_PANELS.DAYS, value);
    }

    getMonthLabel(monthNumber) {
        return [
            this.januaryLabel,
            this.februaryLabel,
            this.marchLabel,
            this.aprilLabel,
            this.mayLabel,
            this.juneLabel,
            this.julyLabel,
            this.augustLabel,
            this.septemberLabel,
            this.octoberLabel,
            this.novemberLabel,
            this.decemberLabel,
        ][monthNumber];
    }

    optionsYears(panel) {
        const arrYears = [];
        const currentYear = moment().year();
        const startYear = this.maxRange ? currentYear - this.maxRange : DEFAULT_START_YEAR;
        for (let i = startYear; i < currentYear + 1; i++) {
            arrYears.push(i.toString());
        }
        if (this.dobSortDescending) {
            arrYears.reverse();
        }
        return this.mapOptions(arrYears, panel);
    }

    optionsMonths(panel) {
        const arrMonth = [];
        for (let i = 0; i < 12; i++) {
            arrMonth.push(this.getMonthLabel(i));
        }
        return this.mapOptions(arrMonth, panel);
    }

    optionsDay(panel) {
        const arrDay = [];
        for (let i = 1; i < 32; i++) {
            arrDay.push(i.toString());
        }
        return this.mapOptions(arrDay, panel);
    }

    mapOptions(arr, panel) {
        return arr.map((option, index) => ({
            value:
                panel === FORMAT_PANELS.MONTHS
                    ? `${(index + 1).toString()}`.padStart(2, '0')
                    : `${option}`.padStart(2, '0'),
            label: option,
        }));
    }

    getSelectOptionsByPanel(panel) {
        switch (panel) {
            case FORMAT_PANELS.YEARS:
                return this.optionsYears(panel);
            case FORMAT_PANELS.MONTHS:
                return this.optionsMonths(panel);
            case FORMAT_PANELS.DAYS:
            default:
                return this.optionsDay(panel);
        }
    }

    getSelectLabelByPanel(panel) {
        const labelMapper = {
            [FORMAT_PANELS.YEARS]: this.yearLabel,
            [FORMAT_PANELS.MONTHS]: this.monthLabel,
            [FORMAT_PANELS.DAYS]: this.dayLabel,
        };

        const existRequiredMark = this.requiredMarkAsPartOfLabel && this.required;

        return labelMapper[panel] + (existRequiredMark ? '*' : '');
    }

    onSelectValue({ detail }, currPanel) {
        const selectedValue = detail && parseInt(detail);

        switch (currPanel) {
            case FORMAT_PANELS.YEARS:
                this.year = selectedValue;
                break;
            case FORMAT_PANELS.MONTHS:
                this.month = selectedValue;
                break;
            case FORMAT_PANELS.DAYS:
            default:
                this.day = selectedValue;
        }
    }

    createSelect = panel => {
        const dayPanel = panel === FORMAT_PANELS.DAYS;
        return html`
            <div class="display-as-select__wrapper">
                <e2-native-select
                    class="native-select native-select--form-field"
                    class-modifier="select--field ${dayPanel ? 'notranslate' : ''}"
                    ?has-errors="${this.isErrorDefined()}"
                    .options="${this.getSelectOptionsByPanel(panel)}"
                    @select=${e => this.onSelectValue(e, panel)}
                    default-value="${this.getChunkOfDate(panel)}"
                    @focus=${this.handleFocus}
                    @blur=${this.throttledBlur}
                    gtm-label="${ifDefined(this.gtmLabel || undefined)}"
                    placeholder="${this.useLabelAsPlaceholder
                        ? this.getSelectLabelByPanel(panel)
                        : ''}"
                    ?disabled-placeholder-option="${this.disabledNativePlaceholderOption}"
                ></e2-native-select>
                <p class="select-field__label">
                    ${this.getSelectLabelByPanel(panel)}
                </p>
            </div>
        `;
    };

    throttledBlur({ skipBlur = false }) {
        // need to use 'throttle' logic because 3 different elements could lose focus without blur
        window.clearTimeout(this.blurTimeoutID);
        if (!skipBlur) {
            this.blurTimeoutID = window.setTimeout(this.handleBlur, 100);
        }
    }

    handleBlur = () => {
        if (!this.fieldValue) {
            return;
        }

        const blurEvent = new CustomEvent('BLUR-FIELD', {
            detail: { fieldName: this.name || this.fieldValue.name },
        });
        this.dispatchEvent(blurEvent);

        if (!this.fieldValue.touched) {
            const touchEvent = new CustomEvent('TOUCH-FIELD', {
                detail: { fieldName: this.name || this.fieldValue.name },
            });
            this.dispatchEvent(touchEvent);
        }
    };

    handleFocus() {
        if (this.fieldValue) {
            const focusEvent = new CustomEvent('FOCUS-FIELD', {
                detail: { fieldName: this.fieldValue.name },
            });
            this.dispatchEvent(focusEvent);
        }

        this.throttledBlur({ skipBlur: true });
    }

    renderAsSelect() {
        return html`
            <div class="display-as-select">
                ${SELECT_ORDER.split('-').map(this.createSelect)}
                <v-slot name="tooltip"></v-slot>
            </div>
        `;
    }

    render() {
        if (this.fieldValue && !this.hideField) {
            return html`
                <div class="datepicker-field">
                    <v-slot name="label"></v-slot>
                    ${this.renderAsSelect()}
                    <div
                        class="datepicker-field__caption"
                        ?hidden="${this.isErrorDefined() || this.hideCaption}"
                    >
                        <v-slot name="caption"></v-slot>
                    </div>
                    <div class="datepicker-field--error">
                        <v-slot name="field-error"></v-slot>
                    </div>
                </div>
            `;
        }
        return html``;
    }
}

const mapStateToProps = (state, ctx) => {
    const selectors = cacheSelector(ctx, getSelectors);
    const country = globalSelectors.getCountry(state);
    const shouldValidateByAge = configSelectors.shouldValidateByAge(state, country);
    return {
        required: selectors.isDataFieldRequired(state, ctx.name),
        hideField: selectors.isDataFieldHidden(state, ctx.name),
        hideCaption: shouldValidateByAge,
        dobSortDescending: configSelectors.dobSortDescending(state),
    };
};

export default () =>
    customElements.define(
        'e2-static-datepicker-field',
        connect(mapStateToProps)(StaticDatePickerField),
    );
